import React, { useState } from 'react';
import { Check, Star, Users, Zap, Crown, Search, TrendingUp, ArrowLeft, Settings } from 'lucide-react';

function Plans() {
    const [billingCycle, setBillingCycle] = useState('monthly');

    const plans = [
        {
            name: 'Basic',
            icon: Users,
            popular: false,
            monthlyPrice: 9,
            yearlyPrice: 90,
            description: 'Perfect for beginners',
            features: [
                'Up to 3 portfolios',
                'Basic analytics',
                'Email support',
                'Mobile access'
            ],
            buttonText: 'Get Started',
            color: 'bg-blue-500'
        },
        {
            name: 'Pro',
            icon: Zap,
            popular: true,
            monthlyPrice: 29,
            yearlyPrice: 290,
            description: 'Most popular choice',
            features: [
                'Unlimited portfolios',
                'Advanced analytics',
                'Priority support',
                'Custom themes',
                'Export features'
            ],
            buttonText: 'Start Free Trial',
            color: 'bg-blue-600'
        },
        {
            name: 'Premium',
            icon: Crown,
            popular: false,
            monthlyPrice: 49,
            yearlyPrice: 490,
            description: 'For professionals',
            features: [
                'Everything in Pro',
                'White-label solution',
                'API access',
                'Dedicated support',
                'Custom integrations'
            ],
            buttonText: 'Contact Sales',
            color: 'bg-blue-700'
        }
    ];

    const getPrice = (plan) => {
        return billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    };

    const getSavings = (plan) => {
        const monthlyTotal = plan.monthlyPrice * 12;
        const yearlyPrice = plan.yearlyPrice;
        return Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
    };

    return (
        <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
            <div className="h-[100vh] overflow-y-scroll p-4 flex-grow max-w-sm min-w-[370px] w-full bg-gray-100
            [&::-webkit-scrollbar]:w-1 
            [&::-webkit-scrollbar-track]:bg-transparent 
            [&::-webkit-scrollbar-thumb]:bg-transparent 
            scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">

                {/* Mobile Header */}
                <header className="bg-white shadow-sm sticky top-0 z-10">
                    <div className="px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <button onClick={() => window.history.back()} className="text-gray-600 mr-3">
                                    <ArrowLeft className="h-6 w-6" />
                                </button>
                                <h1 className="text-lg font-semibold text-gray-900">Subscription Plans</h1>
                            </div>
                            <Settings className="h-6 w-6 text-gray-600" />
                        </div>
                    </div>
                </header>

                {/* Portfolio Summary Card */}
                <div className="px-4 py-4">
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-base font-medium text-gray-900">Current Plan</h2>
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">ACTIVE</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">Plan</span>
                                <div className="font-medium text-gray-900">Free Trial</div>
                            </div>
                            <div>
                                <span className="text-gray-500">Expires</span>
                                <div className="font-medium text-gray-900">7 days left</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Billing Toggle */}
                <div className="px-4 mb-6">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Billing Cycle</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    {billingCycle === 'yearly' ? 'Save up to 17% with yearly billing' : 'Switch to yearly for savings'}
                                </p>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className={`text-xs ${billingCycle === 'monthly' ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                                    Monthly
                                </span>
                                <button
                                    onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                                    className="relative inline-flex h-5 w-9 items-center rounded-full bg-blue-600 transition-colors duration-200"
                                >
                                    <span
                                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${billingCycle === 'yearly' ? 'translate-x-5' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                                <span className={`text-xs ${billingCycle === 'yearly' ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                                    Yearly
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Plans List */}
                <div className="px-4 space-y-4">
                    {plans.map((plan, index) => {
                        const IconComponent = plan.icon;
                        return (
                            <div
                                key={index}
                                className={`bg-white rounded-lg shadow-sm overflow-hidden ${plan.popular ? 'ring-2 ring-blue-500' : ''
                                    }`}
                            >
                                {plan.popular && (
                                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-center py-2">
                                        <div className="flex items-center justify-center">
                                            <Star className="h-3 w-3 mr-1" />
                                            <span className="text-xs font-medium">Most Popular</span>
                                        </div>
                                    </div>
                                )}

                                <div className="p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center">
                                            <div className={`p-2 ${plan.color} rounded-lg mr-3`}>
                                                <IconComponent className="h-4 w-4 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-semibold text-gray-900">{plan.name}</h3>
                                                <p className="text-xs text-gray-500">{plan.description}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xl font-bold text-gray-900">
                                                ${getPrice(plan)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                /{billingCycle === 'monthly' ? 'month' : 'year'}
                                            </div>
                                            {billingCycle === 'yearly' && (
                                                <div className="text-xs text-green-600 font-medium">
                                                    Save {getSavings(plan)}%
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="text-xs font-medium text-gray-700 mb-2">Features included:</h4>
                                        <div className="space-y-1">
                                            {plan.features.map((feature, featureIndex) => (
                                                <div key={featureIndex} className="flex items-center">
                                                    <Check className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                                                    <span className="text-xs text-gray-600">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <button className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-colors duration-200 ${plan.color} hover:opacity-90 text-white`}>
                                        {plan.buttonText}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Features Comparison */}
                <div className="px-4 py-6">
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">Why upgrade?</h3>
                        <div className="space-y-3">
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <TrendingUp className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Advanced Analytics</div>
                                    <div className="text-xs text-gray-500">Track your portfolio performance</div>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <Users className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Priority Support</div>
                                    <div className="text-xs text-gray-500">Get help when you need it</div>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <Crown className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-900">Custom Themes</div>
                                    <div className="text-xs text-gray-500">Personalize your experience</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Navigation */}
                {/* <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
                    <div className="grid grid-cols-5 py-2">
                        {[
                            { icon: TrendingUp, label: 'Market', active: false },
                            { icon: Star, label: 'Watchlist', active: false },
                            { icon: Users, label: 'Portfolio', active: true },
                            { icon: Search, label: 'Order', active: false },
                            { icon: Settings, label: 'Account', active: false }
                        ].map((item, index) => {
                            const IconComponent = item.icon;
                            return (
                                <div key={index} className="flex flex-col items-center py-1">
                                    <IconComponent className={`h-5 w-5 ${item.active ? 'text-blue-600' : 'text-gray-400'}`} />
                                    <span className={`text-xs mt-1 ${item.active ? 'text-blue-600' : 'text-gray-400'}`}>
                                        {item.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div> */}

                {/* Bottom padding to account for fixed navigation */}
                {/* <div className="h-20"></div> */}
            </div>
        </div>
    );
}

export default Plans;